import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import useAsync from 'react-use/esm/useAsync';

type Schedule = {
  id: string;
  name: string;
  enabled: boolean;
  ownerTeam: { id: string; name: string };
};

type OnCallParticipant = {
  name: string;
  type: string;
};

type AlertItem = {
  id: string;
  tinyId: string;
  message: string;
  priority: string;
  status: string;
  acknowledged: boolean;
  createdAt: string;
  owner: string;
};

export type TeamOnCall = {
  scheduleName: string;
  scheduleId: string;
  participants: string[];
  nextParticipant?: string;
  nextStartAt?: string;
};

export type TeamAlert = {
  id: string;
  tinyId: string;
  message: string;
  priority: string;
  acknowledged: boolean;
  createdAt: string;
  owner: string;
};

export type TeamOpsGenieData = {
  onCall: TeamOnCall[];
  alerts: TeamAlert[];
};

const LOG_PREFIX = '[useTeamOpsGenie]';
const CACHE_TTL = 5 * 60 * 1000;
const SCHEDULES_CACHE_TTL = 60 * 60 * 1000;
const cache = new Map<string, { data: TeamOpsGenieData; ts: number }>();
let schedulesCache: { data: Schedule[]; ts: number } | undefined;

async function getSchedules(
  fetchApi: { fetch: typeof fetch },
  proxyBase: string,
): Promise<Schedule[]> {
  if (schedulesCache && Date.now() - schedulesCache.ts < SCHEDULES_CACHE_TTL) {
    return schedulesCache.data;
  }
  const res = await fetchApi.fetch(`${proxyBase}/opsgenie/v2/schedules`);
  if (!res.ok) {
    throw new Error(
      `OpsGenie schedules API returned ${res.status}: ${res.statusText}`,
    );
  }
  const json = await res.json();
  const data: Schedule[] = json.data ?? [];
  schedulesCache = { data, ts: Date.now() };
  return data;
}

export const useTeamOpsGenie = (teamName: string | undefined) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  return useAsync(async (): Promise<TeamOpsGenieData | undefined> => {
    if (!teamName) return undefined;

    const key = teamName.toLowerCase();
    const cached = cache.get(key);
    if (cached && Date.now() - cached.ts < CACHE_TTL) return cached.data;

    const proxyBase = await discoveryApi.getBaseUrl('proxy');

    const allSchedules = await getSchedules(fetchApi, proxyBase);
    const teamSchedules = allSchedules.filter(
      s => s.enabled && s.ownerTeam?.name?.toLowerCase() === key,
    );

    console.debug(
      `${LOG_PREFIX} Found ${teamSchedules.length} schedule(s) for team "${teamName}"`,
    );

    const [onCallResults, alertsRes] = await Promise.all([
      Promise.allSettled(
        teamSchedules.map(async s => {
          const [onCallRes, timelineRes] = await Promise.all([
            fetchApi.fetch(
              `${proxyBase}/opsgenie/v2/schedules/${s.id}/on-calls`,
            ),
            fetchApi.fetch(
              `${proxyBase}/opsgenie/v2/schedules/${s.id}/timeline?interval=7&intervalUnit=days`,
            ),
          ]);

          if (!onCallRes.ok) {
            console.warn(
              `${LOG_PREFIX} On-call request failed for "${s.name}": ${onCallRes.status}`,
            );
            return undefined;
          }

          const onCallJson = await onCallRes.json();
          const participants: string[] = (
            onCallJson.data?.onCallParticipants ?? []
          ).map((p: OnCallParticipant) => p.name);

          let nextParticipant: string | undefined;
          let nextStartAt: string | undefined;
          if (timelineRes.ok) {
            const timelineJson = await timelineRes.json();
            const now = Date.now();
            const currentEmails = new Set(
              participants.map(p => p.toLowerCase()),
            );
            const futurePeriods: Array<{
              startDate: string;
              recipient: { name: string };
            }> = (timelineJson.data?.finalTimeline?.rotations ?? [])
              .flatMap((rot: any) => rot.periods ?? [])
              .filter((p: any) => new Date(p.startDate).getTime() > now)
              .sort(
                (a: any, b: any) =>
                  new Date(a.startDate).getTime() -
                  new Date(b.startDate).getTime(),
              );
            const next = futurePeriods.find(
              p => !currentEmails.has((p.recipient?.name ?? '').toLowerCase()),
            );
            if (next) {
              nextParticipant = next.recipient.name;
              nextStartAt = next.startDate;
            }
          }

          return {
            scheduleName: s.name,
            scheduleId: s.id,
            participants,
            nextParticipant,
            nextStartAt,
          } as TeamOnCall;
        }),
      ),
      fetchApi.fetch(
        `${proxyBase}/opsgenie/v2/alerts?query=${encodeURIComponent(
          `status:open teams:${teamName}`,
        )}&limit=10&sort=createdAt&order=desc`,
      ),
    ]);

    const onCall: TeamOnCall[] = [];
    for (const result of onCallResults) {
      if (result.status === 'fulfilled' && result.value) {
        onCall.push(result.value);
      }
    }

    let alerts: TeamAlert[] = [];
    if (alertsRes.ok) {
      const alertsJson = await alertsRes.json();
      alerts = (alertsJson.data ?? []).map((a: AlertItem) => ({
        id: a.id,
        tinyId: a.tinyId,
        message: a.message,
        priority: a.priority,
        acknowledged: a.acknowledged,
        createdAt: a.createdAt,
        owner: a.owner ?? '',
      }));
    } else {
      console.warn(
        `${LOG_PREFIX} Alerts request failed for "${teamName}": ${alertsRes.status}`,
      );
    }

    const data: TeamOpsGenieData = { onCall, alerts };
    cache.set(key, { data, ts: Date.now() });
    return data;
  }, [teamName, discoveryApi, fetchApi]);
};
